import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib/cn';
import { useFormatters, type DateInput } from '@shared/lib/i18n/formatters';
import { isHijriMonth, type HijriParts } from '@shared/lib/i18n/hijri';

export interface HijriDateProps {
  /** Gregorian instant or date (ISO string). */
  date: DateInput;
  /** Resolved by the API from the organization's Hijri authority table. */
  hijri: HijriParts;
  /** Appended in parentheses when the object's authority differs from the viewer's org. */
  authorityLabel?: string | undefined;
  order?: 'gregorian-first' | 'hijri-first';
  /** Timezone used for the Gregorian date (defaults to the viewer's). */
  timeZone?: string;
  className?: string;
}

export function useHijriText() {
  const { t } = useTranslation('common');
  const f = useFormatters();
  return (hijri: HijriParts): string => {
    const month = isHijriMonth(hijri.month) ? t(`hijri.month.${hijri.month}` as 'hijri.month.1') : f.number(hijri.month);
    const text = t('hijri.date', {
      day: f.number(hijri.day, { useGrouping: false }),
      month,
      year: f.number(hijri.year, { useGrouping: false }),
    });
    return text;
  };
}

/**
 * Gregorian + Hijri pair as one bidi-isolated unit (design system §4.7a). Students, teachers and guardians see the
 * date only: whether a month start is still tentative is managed (and shown) in the admin calendar.
 */
export function HijriDate({ date, hijri, authorityLabel, order = 'gregorian-first', timeZone, className }: HijriDateProps) {
  const { t } = useTranslation('common');
  const f = useFormatters();
  const hijriText = useHijriText();

  const gregorian = f.date(date, 'dateMedium', timeZone);
  let hijriPart = hijriText(hijri);
  if (authorityLabel) hijriPart = t('hijri.withAuthority', { date: hijriPart, authority: authorityLabel });
  const [first, second] = order === 'hijri-first' ? [hijriPart, gregorian] : [gregorian, hijriPart];
  const iso = new Date(date).toISOString().slice(0, 10);

  const content = (
    <bdi className={cn('whitespace-normal', className)}>
      <time dateTime={iso}>{first}</time>
      <span aria-hidden="true" className="mx-1 text-muted">
        ·
      </span>
      <span>{second}</span>
    </bdi>
  );
  return content;
}
